/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * CHON torsion (proper-dihedral) stiffness pack — Phase 1 of the torsion
 * upgrade, companion to mmffBondParameters / mmffAngleParameters.
 *
 * WHAT IT FIXES. With an empty parameter table, every torsion falls back to
 * the engine default (strength = 1 eV ≈ 23 kcal/mol, periodicity = 1, single
 * well at the as-built dihedral). That over-stiff single well freezes each
 * molecule in whatever rotamer it loaded in — no anti↔gauche, no ring flips,
 * no chain reconfiguration — so conformational dynamics can't be studied. This
 * pack supplies realistic BARRIER HEIGHTS and PERIODICITIES per chemistry.
 *
 * KEYING. The periodicity and dominant barrier of a torsion are set mostly by
 * the CENTRAL bond (atoms 2-3 of the 1-2-3-4 dihedral) — its two atom types
 * (from mmffLiteTyping) and its bond order. So entries are keyed on the
 * unordered central type pair + central bond order:
 *   sp³–sp³ single        → 3-fold (n=3), ~1–3 kcal/mol  (ethane/butane class)
 *   sp²–sp² single (conj.) → 2-fold (n=2), prefers planar (butadiene/biaryl)
 *   sp²=sp² double         → 2-fold (n=2), stiff — keeps alkenes planar
 *   aromatic ring          → 2-fold (n=2), stiff — keeps rings planar
 *
 * UNITS. TorsionalBond.compute() evaluates V = ½·strength·(1 − cos(n·φ − γ))
 * with V in eV, so `strength` is the barrier height (peak-to-trough) in eV.
 * Values below are written in kcal/mol and converted.
 *
 * MULTIPLICITY. Each value is the TOTAL barrier about the central bond. Many
 * torsions share one central bond (an sp³–sp³ bond has ~9 H/C–C–H/C torsions),
 * and their barriers add, so the apply pass (mmffParamLookup) divides each
 * value by the number of torsions sharing the bond. Writing totals here keeps
 * the table readable against experiment (ethane ≈ 2.9 kcal/mol about the C–C).
 *
 * REGIME A. This pack carries only height + periodicity; the phase γ is set by
 * the apply pass (mmffParamLookup) from the as-built dihedral so a minimum sits
 * at the loaded conformation (V = 0 at rest). That keeps real barrier heights
 * and the correct number of rotamer wells WITHOUT injecting rest energy — i.e.
 * no NVE blow-up and no minimize-on-load needed. The trade-off is that the
 * wells come out equal-depth, so this captures that rotamers exist and how high
 * the barriers between them are, but not which rotamer is energetically
 * preferred (that needs true OPLS phases + a minimize pass — Phase 2 / Regime B).
 */

const KCAL_TO_EV = 0.043364;
const kc = (kcal: number): number => kcal * KCAL_TO_EV;

export interface TorsionStiffness {
  strength: number; // eV — barrier height (peak-to-trough)
  periodicity: number; // n in V = ½·strength·(1 − cos(n·φ − γ))
}

const torsionKey = (tA: string, tB: string, order: number): string => {
  const [a, b] = tA <= tB ? [tA, tB] : [tB, tA];
  return `${a}~${b}|${order}`;
};

// Keyed by the CENTRAL bond: sorted type pair + bond order.
export const MMFF_LITE_TORSION: Readonly<Record<string, TorsionStiffness>> = {
  // ---- sp³–sp³ single bonds: 3-fold ----
  [torsionKey('C.3', 'C.3', 1)]: { strength: kc(3.0), periodicity: 3 }, // ethane 2.9, butane 3.4
  [torsionKey('C.3', 'N.3', 1)]: { strength: kc(2.0), periodicity: 3 }, // methylamine ~2
  [torsionKey('C.3', 'O.3', 1)]: { strength: kc(1.1), periodicity: 3 }, // methanol ~1.1
  [torsionKey('N.3', 'N.3', 1)]: { strength: kc(1.5), periodicity: 3 },
  [torsionKey('O.3', 'O.3', 1)]: { strength: kc(1.1), periodicity: 3 }, // peroxide

  // ---- sp³–sp² single: low 3-fold (allylic) ----
  [torsionKey('C.3', 'C.2', 1)]: { strength: kc(0.5), periodicity: 3 },
  [torsionKey('C.3', 'C.ar', 1)]: { strength: kc(0.2), periodicity: 3 }, // toluene methyl ~free

  // ---- sp²–sp² single (conjugated): 2-fold, prefers planar ----
  [torsionKey('C.2', 'C.2', 1)]: { strength: kc(3.0), periodicity: 2 }, // butadiene s-cis/s-trans
  [torsionKey('C.2', 'O.3', 1)]: { strength: kc(3.0), periodicity: 2 }, // ester/acid C–O
  [torsionKey('C.2', 'N.3', 1)]: { strength: kc(4.0), periodicity: 2 }, // amide-ish planarity
  [torsionKey('C.ar', 'N.3', 1)]: { strength: kc(1.5), periodicity: 2 }, // aniline / biaryl-N

  // ---- double bonds: 2-fold, stiff (keep planar) ----
  [torsionKey('C.2', 'C.2', 2)]: { strength: kc(7.0), periodicity: 2 }, // alkene C=C
  [torsionKey('C.2', 'N.2', 2)]: { strength: kc(7.0), periodicity: 2 }, // imine C=N

  // ---- aromatic ring: 2-fold, stiff (keep ring planar) ----
  [torsionKey('C.ar', 'C.ar', 1)]: { strength: kc(7.0), periodicity: 2 },
  [torsionKey('C.ar', 'C.ar', 2)]: { strength: kc(7.0), periodicity: 2 },
};

/**
 * Look up torsion height + periodicity for a central type pair at a given bond
 * order. Returns null when not in the pack (caller uses genericTorsion).
 */
export const lookupTorsionStiffness = (tA: string, tB: string, order: number): TorsionStiffness | null => {
  const v = MMFF_LITE_TORSION[torsionKey(tA, tB, order)];
  return v === undefined ? null : v;
};

/**
 * Generic fallback by central bond order, so EVERY torsion gets a sensible
 * value instead of the over-stiff k=1 default: mild 3-fold for single bonds,
 * stiff 2-fold for double/aromatic (planarity), and null for triple bonds
 * (a linear center has no defined torsion — caller zeroes it).
 */
export const genericTorsion = (order: number): TorsionStiffness | null => {
  if (order >= 3) return null;
  if (order === 2) return { strength: kc(6.0), periodicity: 2 };
  return { strength: kc(1.0), periodicity: 3 };
};
